import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { appRoute } from "./route";
import { PageContainer, PageHeader } from "@fleet/ui";
import { PosTerminal } from "@/features/pos/components/pos-terminal";
import { RestaurantGate } from "@/features/pos/components/restaurant-gate";
import { useActiveContext } from "@/features/pos/hooks/use-active-context";
import { useI18n } from "@/providers/i18n-provider";

/**
 * POS search params — make state SHAREABLE by URL (campaign #9):
 *   - `order`: active order (open ticket). Copy/pasting URL reopens that
 *     ticket on another device/tab; if id no longer exists or is inaccessible
 *     (sync rules), POS self-heals to "Start a new order" (no hung loader).
 *   - `category`: product grid category tab.
 * We omit empty values to keep URLs clean.
 */
interface PosSearch {
  order?: string;
  category?: string;
}

function PosPage() {
  const { t } = useI18n();
  const ctx = useActiveContext();
  const search = useSearch({ from: posRoute.id });
  const navigate = useNavigate();

  return (
    <PageContainer maxWidth="5xl">
      <PageHeader title={t(msg`Point of sale`)} />
      <RestaurantGate ctx={ctx}>
        {(c) => (
          <PosTerminal
            org={c.org}
            branch={c.branch}
            orderParam={() => search().order}
            categoryParam={() => search().category}
            onOrderChange={(order) => {
              void navigate({
                to: "/pos",
                search: (prev: PosSearch) => ({ ...prev, order: order ?? undefined }),
                replace: true,
              });
            }}
            onCategoryChange={(category) => {
              void navigate({
                to: "/pos",
                search: (prev: PosSearch) => ({ ...prev, category: category ?? undefined }),
                replace: true,
              });
            }}
          />
        )}
      </RestaurantGate>
    </PageContainer>
  );
}

export const posRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/pos",
  validateSearch: (search: Record<string, unknown>): PosSearch => ({
    order:
      typeof search.order === "string" && search.order.trim() !== "" ? search.order : undefined,
    category:
      typeof search.category === "string" && search.category.trim() !== ""
        ? search.category
        : undefined,
  }),
  component: PosPage,
});
